import React, { useState, useEffect, useContext } from "react";
import {
  Page,
  Layout,
  Modal,
  TextContainer,
  SkeletonPage,
  Card,
  SkeletonBodyText,
  SkeletonDisplayText,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import ThemeCard from "./index/ThemeCard";
import LibraryThemeCard from "./index/LibraryThemeCard";
import { AppContext } from "../../components/global/appContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { TitleBar } from "@shopify/app-bridge-react";
import lmsAPI from "../../services/lmsAPI";

export default function ThemesIndex(props) {
  const { t } = useTranslation();
  const [toPublishThemeKey, setToPublishThemeKey] = useState(null);

  const appContext = useContext(AppContext);
  const queryClient = useQueryClient();

  async function getThemeData() {
    const res = await lmsAPI.get("/themes");
    return res.data;
  }
  const { data, isError, isLoading } = useQuery(["themes"], getThemeData);

  async function publishThemeRequest(themeKey) {
    const res = await lmsAPI.post(`/themes/publish/${themeKey}`);
    return res.data;
  }

  const publishTheme = useMutation(publishThemeRequest, {
    onSuccess: (data) => {
      queryClient.setQueryData(["themes"], data);
      appContext.setMessage(t("themes.messages.publishSuccess"));
    },
    onError: () => {
      appContext.setMessage(t("themes.messages.publishError"), true);
    },
    onSettled: () => {
      setToPublishThemeKey(null);
    },
  });

  if (isLoading) {
    return (
      <SkeletonPage title={t("themes.title")}>
        <Layout>
          <Layout.AnnotatedSection
            title={t("themes.currentTheme.title")}
            description={t("themes.skeleton.currentThemeDescription")}
          >
            <Card sectioned title={<SkeletonDisplayText />}>
              <SkeletonBodyText lines={8} />
            </Card>
          </Layout.AnnotatedSection>
          <Layout.AnnotatedSection
            title={t("themes.themeLibrary.title")}
            description={t("themes.themeLibrary.description")}
          >
            <Card sectioned>
              <SkeletonBodyText />
            </Card>
            <Card sectioned>
              <SkeletonBodyText />
            </Card>
            <Card sectioned>
              <SkeletonBodyText />
            </Card>
          </Layout.AnnotatedSection>
        </Layout>
      </SkeletonPage>
    );
  }

  if (isError) {
    return <p>{t("themes.messages.error")}</p>;
  }

  const publishModal = (
    <Modal
      open={toPublishThemeKey !== null}
      onClose={() => setToPublishThemeKey(null)}
      title={t("themes.modal.overrideTitle")}
      primaryAction={{
        content: t("themes.modal.override"),
        destructive: true,
        onAction: () => publishTheme.mutate(toPublishThemeKey),
        loading: publishTheme.isLoading,
      }}
      secondaryActions={[
        {
          content: t("themes.modal.cancel"),
          onAction: () => setToPublishThemeKey(null),
          loading: publishTheme.isLoading,
        },
      ]}
    >
      <Modal.Section>
        <TextContainer>
          <p>{t("themes.modal.undoWarning")}</p>
        </TextContainer>
      </Modal.Section>
    </Modal>
  );

  // function publishTheme(themeKey) {
  //   setLoading(true);
  //   axios
  //     .post(`/themes/publish/${themeKey}`)
  //     .then((res) => {
  //       setPublishedTheme(res.data.live_theme);
  //       setLibraryThemes(res.data.library_themes);
  //     })
  //     .catch(() => {
  //       appContext.setMessage("Couldnt publish theme", true);
  //     })
  //     .finally(() => {
  //       setLoading(false);
  //       setToPublishThemeKey(null);
  //     });
  // }

  return (
    <Page>
      <TitleBar title={t("themes.title")} secondaryActions={[]} />
      <Layout>
        <Layout.AnnotatedSection
          title={t("themes.currentTheme.title")}
          description={t("themes.currentTheme.description")}
        >
          <ThemeCard
            theme={data.live_theme}
            hasCustomThemeAccess={data.custom_theme_access}
            loading={publishTheme.isLoading}
          />
        </Layout.AnnotatedSection>
        <Layout.AnnotatedSection
          title={t("themes.themeLibrary.title")}
          description={t("themes.themeLibrary.description")}
        >
          {data.library_themes.map((theme) => (
            <LibraryThemeCard
              key={theme.key}
              theme={theme}
              publishTheme={setToPublishThemeKey}
              loading={publishTheme.isLoading}
            />
          ))}
        </Layout.AnnotatedSection>
      </Layout>
      {publishModal}
    </Page>
  );
}
